import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  UsersIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { employeeSurveyReportService } from '../../services/employeeSurveyReportService'; // Import the correct service

/* UI-only port. Nothing removed: survey picker, DD/MM/YYYY date parsing,
   employee search + responded/pending filter, pagination, select-all,
   report generation, Excel/PDF export, collapsible category charts,
   per-question rating bars and the detailed per-employee sections are
   all still here with identical behaviour.

   Presentation changes:
     - blue/purple gradients and rounded-2xl cards become flat hairline
       panels on the teal token palette
     - the left filter column becomes an Odoo sidebar of stacked boxes
     - rating colours keep their 1→5 traffic-light scale but are drawn
       from the badge tokens, so they hold up in dark mode
     - density tightens to 12.5px body / 8px rows
*/

const EmployeeSurveyReport = () => {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Export state
  const [exportFormat, setExportFormat] = useState(null); // 'excel' | 'pdf' | null while exporting
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportDropdownRef = useRef(null);

  const surveyDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(event.target)) {
        setSurveyDropdownOpen(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    if (selectedSurvey) {
      loadEmployees();
    }
  }, [selectedSurvey, fromDate, toDate]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const data = await employeeSurveyReportService.getSurveys();
      setSurveys(data);
    } catch (err) {
      setError('Failed to load surveys');
      console.error('Error loading surveys:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      if (!selectedSurvey) return;

      const filters = {
        companyId: selectedSurvey.companyId,
        surveyId: selectedSurvey.id,
        fromDate: fromDate ? parseDDMMYYYY(fromDate) : null,
        toDate: toDate ? parseDDMMYYYY(toDate) : null
      };

      console.log('🔄 Loading employees with filters:', filters);

      const data = await employeeSurveyReportService.getEmployees(filters);

      // Transform employees data to ensure displayName exists
      const transformedEmployees = data.map(emp => ({
        ...emp,
        displayName: emp.displayName || `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`
      }));

      console.log('✅ Employees loaded:', transformedEmployees.length);
      setEmployees(transformedEmployees);
      setSelectedEmployees([]);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('❌ Error loading employees:', err);
      setError('Failed to load employees: ' + err.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedSurvey) {
      setError('Please select a survey');
      return;
    }

    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      const request = {
        surveyId: parseInt(selectedSurvey.id),
        employeeIds: selectedEmployees,
        fromDate: fromDate ? parseDDMMYYYY(fromDate) : null,
        toDate: toDate ? parseDDMMYYYY(toDate) : null
      };

      console.log('🔄 Generating report with request:', request);
      const data = await employeeSurveyReportService.generateReport(request);
      setReportData(data);
      console.log('✅ Report generated successfully');
    } catch (err) {
      console.error('❌ Error generating report:', err);
      setError('Failed to generate report: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedSurvey) {
      setError('Please select a survey');
      return;
    }

    setExportMenuOpen(false);
    setError(null);
    try {
      setExportFormat(format);
      const request = {
        surveyId: parseInt(selectedSurvey.id),
        employeeIds: selectedEmployees,
        fromDate: fromDate ? parseDDMMYYYY(fromDate) : null,
        toDate: toDate ? parseDDMMYYYY(toDate) : null
      };
      await employeeSurveyReportService.exportReport(request, format);
    } catch (err) {
      console.error('❌ Error exporting report:', err);
      setError('Failed to export report: ' + err.message);
    } finally {
      setExportFormat(null);
    }
  };

  // Date format utilities
  const formatToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const parseDDMMYYYY = (dateString) => {
    if (!dateString) return null;
    try {
      const [day, month, year] = dateString.split('/');
      // Create date in UTC to avoid timezone issues
      return new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
    } catch {
      return null;
    }
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-');
      setFromDate(`${day}/${month}/${year}`);
    } else {
      setFromDate('');
    }
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-');
      setToDate(`${day}/${month}/${year}`);
    } else {
      setToDate('');
    }
  };

  const getInputDateValue = (ddmmyyyyString) => {
    if (!ddmmyyyyString) return '';
    try {
      const [day, month, year] = ddmmyyyyString.split('/');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  // Employee selection utilities
  const handleEmployeeSelection = (employeeId) => {
    const newSelected = selectedEmployees.includes(employeeId)
      ? selectedEmployees.filter(id => id !== employeeId)
      : [...selectedEmployees, employeeId];

    setSelectedEmployees(newSelected);
  };

  const handleSelectAllEmployees = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(emp => emp.id));
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatToDDMMYYYY(dateString);
  };

  /* 1→5 traffic-light scale, now token-based so it survives dark mode.
     Same five steps, same ordering as the old gradient classes. */
  const RATING_TONE = { 1: 'red', 2: 'red', 3: 'amber', 4: 'green', 5: 'green' };

  const ratingFill = (rating) => {
    const map = {
      1: 'var(--b-red-fg)',
      2: 'var(--b-red-fg)',
      3: 'var(--b-amber-fg)',
      4: 'var(--pos)',
      5: 'var(--pos)'
    };
    return map[rating] || 'var(--muted)';
  };

  const ratingPill = (rating) => {
    const tone = RATING_TONE[rating] || 'grey';
    return {
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: 10,
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      background: `var(--b-${tone}-bg)`,
      color: `var(--b-${tone}-fg)`
    };
  };

  const panel = {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 4,
    boxShadow: 'var(--shadow-sm)'
  };

  const ghostBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    height: 28,
    padding: '0 10px',
    background: 'var(--surface)',
    border: '1px solid var(--line-3)',
    borderRadius: 3,
    color: 'var(--body-2)',
    fontSize: 12.5,
    cursor: 'pointer'
  };

  const fieldInput = {
    width: '100%',
    height: 30,
    padding: '0 8px',
    background: 'var(--surface)',
    border: '1px solid var(--line-3)',
    borderRadius: 3,
    color: 'var(--body)',
    fontSize: 12.5,
    outline: 'none'
  };

  const sectionHead = {
    padding: '9px 12px',
    borderBottom: '1px solid var(--line-2)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)'
  };

  const ProgressBar = ({ percentage, fill }) => (
    <div
      className="h-[7px] w-full overflow-hidden rounded-[3px]"
      style={{ background: 'var(--track)' }}
    >
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percentage}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="h-full rounded-[3px]"
        style={{ background: fill }}
      />
    </div>
  );

  const RatingDistributionChart = ({ distribution }) => (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map(rating => (
        <div key={rating} className="flex items-center gap-2.5">
          <span className="w-6 text-[11.5px] font-semibold" style={{ color: 'var(--faint)' }}>
            R{rating}
          </span>
          <div className="flex-1">
            <ProgressBar
              percentage={distribution[`rating${rating}`] || 0}
              fill={ratingFill(rating)}
            />
          </div>
          <span className="w-9 text-right text-[11.5px]" style={{ color: 'var(--muted)' }}>
            {Math.round(distribution[`rating${rating}`] || 0)}%
          </span>
        </div>
      ))}
    </div>
  );

  // Search and filter logic for employees
  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(employee =>
        employee.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType === 'responded') {
      filtered = filtered.filter(employee => employee.responseDate);
    } else if (filterType === 'pending') {
      filtered = filtered.filter(employee => !employee.responseDate);
    }

    return filtered;
  }, [employees, searchTerm, filterType]);

  // Pagination logic
  const totalItems = filteredEmployees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilterType('all');
    setCurrentPage(1);
  };

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <div className="flex min-w-0 items-baseline gap-[7px]">
            <span className="hidden text-[13px] sm:block" style={{ color: 'var(--accent)' }}>Reports</span>
            <span className="hidden sm:block" style={{ color: 'var(--dim)' }}>/</span>
            <h1
              className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
              style={{ color: 'var(--ink)' }}
            >
              Employee Survey Reports
            </h1>
          </div>

          <div className="flex items-center gap-[6px]">
            <button
              onClick={generateReport}
              disabled={generating || !selectedSurvey || selectedEmployees.length === 0}
              className="inline-flex h-7 items-center gap-[5px] rounded-[3px] px-[13px] text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
            >
              {generating ? (
                <>
                  <span
                    className="h-3.5 w-3.5 animate-spin rounded-full border-b-2"
                    style={{ borderColor: 'var(--on-accent)' }}
                  />
                  Generating…
                </>
              ) : (
                <>
                  <ChartBarIcon className="h-[13px] w-[13px]" />
                  Generate
                </>
              )}
            </button>

            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setExportMenuOpen((open) => !open)}
                disabled={exportFormat !== null || !reportData}
                style={{ ...ghostBtn, opacity: exportFormat !== null || !reportData ? 0.5 : 1 }}
              >
                {exportFormat ? (
                  <span
                    className="h-3.5 w-3.5 animate-spin rounded-full border-b-2"
                    style={{ borderColor: 'var(--accent)' }}
                  />
                ) : (
                  <ArrowDownTrayIcon className="h-[13px] w-[13px]" />
                )}
                {exportFormat ? `Exporting ${exportFormat.toUpperCase()}…` : 'Export'}
                <ChevronDownIcon className="h-[11px] w-[11px]" />
              </button>
              {exportMenuOpen && (
                <div
                  className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-[4px]"
                  style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
                >
                  <button
                    onClick={() => handleExport('excel')}
                    className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px]"
                    style={{ color: 'var(--body)', background: 'transparent', border: 0, cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <TableCellsIcon className="h-4 w-4" style={{ color: 'var(--b-green-fg)' }} />
                    Export to Excel
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="flex w-full items-center gap-2 px-3 py-[7px] text-left text-[12.5px]"
                    style={{ color: 'var(--body)', background: 'transparent', border: 0, cursor: 'pointer', borderTop: '1px solid var(--line-soft)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <DocumentTextIcon className="h-4 w-4" style={{ color: 'var(--b-red-fg)' }} />
                    Export to PDF
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-[60px] flex-1" />

          {/* Survey scope facet */}
          <div className="flex items-center gap-2" ref={surveyDropdownRef}>
            <span className="text-[11.5px] font-semibold uppercase tracking-[.07em]" style={{ color: 'var(--faint)' }}>
              Survey
            </span>
            <div className="relative">
              <button
                onClick={() => setSurveyDropdownOpen(!surveyDropdownOpen)}
                className="inline-flex h-7 max-w-[260px] items-center gap-2 rounded-[3px] px-2.5 text-[12.5px] font-semibold"
                style={
                  selectedSurvey
                    ? { background: 'var(--tint)', color: 'var(--tint-ink)', border: 0, cursor: 'pointer' }
                    : { background: 'var(--surface)', color: 'var(--body-2)', border: '1px solid var(--line-3)', cursor: 'pointer' }
                }
              >
                <span className="truncate">{selectedSurvey ? selectedSurvey.name : 'Select survey'}</span>
                <ChevronDownIcon
                  className="h-[11px] w-[11px] flex-shrink-0 transition-transform"
                  style={{ transform: surveyDropdownOpen ? 'rotate(180deg)' : 'none' }}
                />
              </button>

              {surveyDropdownOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 max-h-60 min-w-[260px] overflow-y-auto rounded-[4px]"
                  style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
                >
                  {surveys.length === 0 ? (
                    <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--faint)' }}>
                      No surveys available
                    </div>
                  ) : (
                    surveys.map((survey) => {
                      const active = selectedSurvey?.id === survey.id;
                      return (
                        <button
                          key={survey.id}
                          onClick={() => {
                            setSelectedSurvey(survey);
                            setSurveyDropdownOpen(false);
                          }}
                          className="w-full px-3 py-2 text-left"
                          style={{
                            background: active ? 'var(--row-sel)' : 'transparent',
                            border: 0,
                            borderBottom: '1px solid var(--line-soft)',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--hover)'; }}
                          onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div
                            className="text-[12.5px]"
                            style={{ color: active ? 'var(--tint-ink)' : 'var(--ink)', fontWeight: active ? 600 : 500 }}
                          >
                            {survey.name}
                          </div>
                          <div className="mt-0.5 text-[11px]" style={{ color: 'var(--faint)' }}>
                            {survey.description || 'No description'}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-[14px]">
        {/* Error Alert */}
        {error && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--b-red-bg)', border: '1px solid var(--line)', color: 'var(--b-red-fg)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Main Content */}
        {selectedSurvey ? (
          <div className="grid grid-cols-1 gap-[14px] xl:grid-cols-4">
            {/* ── Left column: filters ── */}
            <div className="space-y-[14px] xl:col-span-1">
              {/* Date Range */}
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                style={panel}
              >
                <div style={sectionHead}>
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="h-[14px] w-[14px]" style={{ color: 'var(--accent)' }} />
                    Date range
                  </span>
                </div>
                <div className="space-y-3 p-3">
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold" style={{ color: 'var(--body-2)' }}>
                      From
                    </label>
                    <input
                      type="date"
                      value={getInputDateValue(fromDate)}
                      onChange={handleFromDateChange}
                      style={fieldInput}
                    />
                    {fromDate && (
                      <div className="mt-1 text-[11px]" style={{ color: 'var(--pos)' }}>{fromDate}</div>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold" style={{ color: 'var(--body-2)' }}>
                      To
                    </label>
                    <input
                      type="date"
                      value={getInputDateValue(toDate)}
                      onChange={handleToDateChange}
                      style={fieldInput}
                    />
                    {toDate && (
                      <div className="mt-1 text-[11px]" style={{ color: 'var(--pos)' }}>{toDate}</div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Employees */}
              {employees.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 }}
                  style={panel}
                >
                  <div style={sectionHead} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <UsersIcon className="h-[14px] w-[14px]" style={{ color: 'var(--accent)' }} />
                      Employees
                      <span className="font-normal" style={{ color: 'var(--faint)' }}>
                        {selectedEmployees.length}/{filteredEmployees.length}
                      </span>
                    </span>
                    <button
                      onClick={handleSelectAllEmployees}
                      className="text-[11.5px] font-medium"
                      style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--accent)' }}
                    >
                      {selectedEmployees.length === filteredEmployees.length ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>

                  {/* Search + filter */}
                  <div className="space-y-2 p-3" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <div
                      className="flex items-center gap-1.5 rounded-[3px] px-2"
                      style={{ border: '1px solid var(--line-3)', background: 'var(--surface)' }}
                    >
                      <MagnifyingGlassIcon className="h-[13px] w-[13px] flex-shrink-0" style={{ color: 'var(--faint)' }} />
                      <input
                        type="text"
                        placeholder="Search employees…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="h-[26px] min-w-0 flex-1 border-0 bg-transparent text-[12.5px] outline-none"
                        style={{ color: 'var(--body)' }}
                      />
                      {searchTerm && (
                        <button
                          onClick={clearSearch}
                          style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--faint)' }}
                          aria-label="Clear search"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      <FunnelIcon className="h-[13px] w-[13px] flex-shrink-0" style={{ color: 'var(--faint)' }} />
                      {[
                        { v: 'all', label: 'All' },
                        { v: 'responded', label: 'Responded' },
                        { v: 'pending', label: 'Pending' }
                      ].map((opt, i, arr) => {
                        const active = filterType === opt.v;
                        return (
                          <button
                            key={opt.v}
                            onClick={() => { setFilterType(opt.v); setCurrentPage(1); }}
                            className="h-[22px] px-2 text-[11.5px]"
                            style={{
                              background: active ? 'var(--tint)' : 'var(--surface)',
                              color: active ? 'var(--tint-ink)' : 'var(--muted)',
                              fontWeight: active ? 600 : 500,
                              border: '1px solid var(--line-3)',
                              borderRadius: i === 0 ? '3px 0 0 3px' : i === arr.length - 1 ? '0 3px 3px 0' : 0,
                              marginLeft: i === 0 ? 0 : -1,
                              cursor: 'pointer'
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Employee rows */}
                  <div className="max-h-80 overflow-y-auto">
                    {currentEmployees.map(employee => {
                      const checked = selectedEmployees.includes(employee.id);
                      return (
                        <div
                          key={employee.id}
                          className="flex cursor-pointer items-center gap-2.5 px-3 py-2"
                          style={{
                            background: checked ? 'var(--row-sel)' : 'transparent',
                            borderBottom: '1px solid var(--line-soft)'
                          }}
                          onClick={() => handleEmployeeSelection(employee.id)}
                          onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'var(--hover)'; }}
                          onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {}}
                            className="h-[13px] w-[13px] flex-shrink-0 cursor-pointer"
                            style={{ accentColor: 'var(--accent)' }}
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className="truncate text-[12.5px]"
                              style={{ color: 'var(--ink)', fontWeight: checked ? 600 : 500 }}
                            >
                              {employee.displayName}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px]" style={{ color: 'var(--faint)' }}>
                              <span className="truncate">{employee.position}</span>
                              <span>·</span>
                              <span className="truncate">{employee.departmentName}</span>
                              <span>·</span>
                              <span style={{ color: employee.responseDate ? 'var(--pos)' : 'var(--b-amber-fg)' }}>
                                {employee.responseDate ? 'Responded' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pager */}
                  {filteredEmployees.length > 0 && (
                    <div
                      className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                      style={{ borderTop: '1px solid var(--line-2)' }}
                    >
                      <div className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
                        {startIndex + 1}-{Math.min(endIndex, totalItems)} / {totalItems}
                      </div>
                      <div className="flex">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="flex h-[23px] w-[23px] items-center justify-center disabled:cursor-not-allowed disabled:opacity-40"
                          style={{
                            background: 'var(--surface)', border: '1px solid var(--line-3)', borderRight: 0,
                            borderRadius: '3px 0 0 3px', color: 'var(--body-2)', cursor: 'pointer'
                          }}
                          aria-label="Previous page"
                        >
                          <ChevronLeftIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="flex h-[23px] w-[23px] items-center justify-center disabled:cursor-not-allowed disabled:opacity-40"
                          style={{
                            background: 'var(--surface)', border: '1px solid var(--line-3)',
                            borderRadius: '0 3px 3px 0', color: 'var(--body-2)', cursor: 'pointer'
                          }}
                          aria-label="Next page"
                        >
                          <ChevronRightIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* ── Right column: report ── */}
            <div className="space-y-[14px] xl:col-span-3">
              <AnimatePresence>
                {reportData && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    className="space-y-[14px]"
                  >
                    {/* Report header */}
                    <div style={panel}>
                      <div className="p-[14px]" style={{ borderBottom: '1px solid var(--line-2)' }}>
                        <h2 className="text-[17px] font-semibold" style={{ color: 'var(--ink)' }}>
                          {reportData.survey.name}
                        </h2>
                        {reportData.survey.description && (
                          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--body-2)' }}>
                            {reportData.survey.description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px]" style={{ color: 'var(--faint)' }}>
                          <span>
                            Period {formatDate(reportData.reportPeriod.fromDate)} — {formatDate(reportData.reportPeriod.toDate)}
                          </span>
                          <span>Employees {reportData.totalEmployees}</span>
                          <span>Responses {reportData.totalResponses}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-[14px] p-[14px] lg:grid-cols-2">
                        <div>
                          <h3 className="mb-3 text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
                            Overall rating distribution
                          </h3>
                          <RatingDistributionChart distribution={reportData.overallRatingDistribution} />
                        </div>

                        <div>
                          <h3 className="mb-3 text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
                            Summary
                          </h3>
                          <div className="space-y-0">
                            {[
                              ['Total employees', reportData.totalEmployees],
                              ['Total responses', reportData.totalResponses],
                              ['Response rate', `${((reportData.totalResponses / reportData.totalEmployees) * 100).toFixed(1)}%`]
                            ].map(([label, value]) => (
                              <div
                                key={label}
                                className="flex items-center justify-between py-[7px] text-[12.5px]"
                                style={{ borderBottom: '1px solid var(--line-soft)' }}
                              >
                                <span style={{ color: 'var(--body-2)' }}>{label}</span>
                                <span className="font-semibold" style={{ color: 'var(--ink)' }}>{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category charts */}
                    {reportData.categoryCharts && reportData.categoryCharts.map(category => {
                      const open = expandedCategories.has(category.categoryId);
                      return (
                        <motion.div
                          key={category.categoryId}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="overflow-hidden"
                          style={panel}
                        >
                          <div
                            className="flex cursor-pointer items-center justify-between px-[14px] py-2.5"
                            style={{
                              background: open ? 'var(--surface-3)' : 'var(--surface)',
                              borderBottom: open ? '1px solid var(--line-2)' : 'none'
                            }}
                            onClick={() => toggleCategory(category.categoryId)}
                          >
                            <h3 className="text-[13.5px] font-semibold" style={{ color: 'var(--ink)' }}>
                              {category.categoryName}
                            </h3>
                            {open
                              ? <ChevronUpIcon className="h-4 w-4" style={{ color: 'var(--muted)' }} />
                              : <ChevronDownIcon className="h-4 w-4" style={{ color: 'var(--muted)' }} />}
                          </div>

                          <AnimatePresence>
                            {open && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.25 }}
                              >
                                <div className="grid grid-cols-1 gap-[14px] p-[14px] lg:grid-cols-2">
                                  <div>
                                    <h4 className="mb-3 text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
                                      Question averages
                                    </h4>
                                    <div>
                                      {category.averageRatings && category.averageRatings.map(question => (
                                        <div
                                          key={question.questionId}
                                          className="flex items-center justify-between gap-3 py-2"
                                          style={{ borderBottom: '1px solid var(--line-soft)' }}
                                        >
                                          <div className="min-w-0 flex-1">
                                            <p className="text-[12.5px]" style={{ color: 'var(--body)' }}>
                                              {question.questionText}
                                            </p>
                                            <p className="mt-0.5 text-[11px]" style={{ color: 'var(--faint)' }}>
                                              {question.totalResponses} responses
                                            </p>
                                          </div>
                                          <span style={ratingPill(Math.round(question.averageRating))}>
                                            {question.averageRating.toFixed(1)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className="mb-3 text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
                                      Rating distribution
                                    </h4>
                                    <RatingDistributionChart distribution={category.ratingDistribution} />
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}

                    {/* Detailed employee reports */}
                    {reportData.employeeReports && reportData.employeeReports.map((employeeReport, index) => (
                      <motion.div
                        key={employeeReport.employee.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06 }}
                        className="overflow-hidden"
                        style={panel}
                      >
                        <div
                          className="flex flex-wrap items-center justify-between gap-2 px-[14px] py-2.5"
                          style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--line-2)' }}
                        >
                          <div className="min-w-0">
                            <h3 className="truncate text-[13.5px] font-semibold" style={{ color: 'var(--ink)' }}>
                              {employeeReport.employee.displayName}
                            </h3>
                            <p className="text-[11.5px]" style={{ color: 'var(--faint)' }}>
                              {employeeReport.employee.position} · {employeeReport.employee.departmentName}
                            </p>
                          </div>
                          <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
                            {formatDate(employeeReport.responseDate)}
                          </span>
                        </div>

                        <div className="p-[14px]">
                          {employeeReport.categoryWiseData && employeeReport.categoryWiseData.map(category => (
                            <div key={category.categoryId} className="mb-4 last:mb-0">
                              <h4
                                className="mb-2.5 pb-1.5 text-[12.5px] font-semibold"
                                style={{ color: 'var(--ink)', borderBottom: '1px solid var(--line-2)' }}
                              >
                                {category.categoryName}
                              </h4>
                              <div className="space-y-2.5">
                                {category.questions && category.questions.map(question => (
                                  <div
                                    key={question.questionId}
                                    className="rounded-[3px] p-2.5"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
                                  >
                                    <p className="mb-2 text-[12.5px] font-medium" style={{ color: 'var(--ink)' }}>
                                      {question.questionText}
                                    </p>

                                    {question.questionType === 1 && question.ratingValue && ( // RatingScale
                                      <div className="flex items-center gap-3">
                                        <span style={ratingPill(question.ratingValue)}>{question.ratingValue}</span>
                                        <div className="flex-1">
                                          <ProgressBar
                                            percentage={(question.ratingValue / 5) * 100}
                                            fill={ratingFill(question.ratingValue)}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {question.answer && (
                                      <p
                                        className="mt-1.5 rounded-[3px] p-2.5 text-[12.5px]"
                                        style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', color: 'var(--body-2)' }}
                                      >
                                        {question.answer}
                                      </p>
                                    )}

                                    {question.selectedOptions && (
                                      <p
                                        className="mt-1.5 rounded-[3px] p-2.5 text-[12.5px]"
                                        style={{ background: 'var(--surface)', border: '1px solid var(--line-soft)', color: 'var(--body-2)' }}
                                      >
                                        Selected: {question.selectedOptions}
                                      </p>
                                    )}

                                    {question.ratingDistribution && (
                                      <div className="mt-2.5">
                                        <RatingDistributionChart distribution={question.ratingDistribution} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Empty State */}
              {selectedSurvey && !reportData && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="px-6 py-14 text-center"
                  style={panel}
                >
                  <ChartBarIcon className="mx-auto mb-3 h-10 w-10" style={{ color: 'var(--dim)' }} />
                  <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>
                    No report generated
                  </h3>
                  <p className="mx-auto max-w-md text-[12.5px]" style={{ color: 'var(--faint)' }}>
                    Select employees on the left, then hit Generate.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        ) : (
          /* Empty State — No Survey Selected */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="px-6 py-14 text-center"
            style={panel}
          >
            <DocumentTextIcon className="mx-auto mb-3 h-10 w-10" style={{ color: 'var(--dim)' }} />
            <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>
              No survey selected
            </h3>
            <p className="mx-auto max-w-md text-[12.5px]" style={{ color: 'var(--faint)' }}>
              Pick a survey in the control panel above to load its respondents.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EmployeeSurveyReport;
